import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Euro, ShieldCheck, Clock, ArrowRight, ArrowLeftRight, MapPin, Users, Loader2, CalendarDays } from "lucide-react";
import api from "../lib/api";
import SEOHead from "../components/SEOHead";
import { DriverSnapshot } from "./CarpoolProfile";

const ROUTES = {
  "bruxelles-paris": { from: "Bruxelles", to: "Paris", reverse: "paris-bruxelles", fromSpot: "Gare du Midi, Porte de Hal, Schuman", toSpot: "Porte de la Chapelle, Gare du Nord, La Défense" },
  "paris-bruxelles": { from: "Paris", to: "Bruxelles", reverse: "bruxelles-paris", fromSpot: "Porte de la Chapelle, Gare du Nord, La Défense", toSpot: "Gare du Midi, Porte de Hal, Schuman" },
};
const ROAD_KM = 315;
const DURATION = "3 h 15";
const SEATS_REF = 3;

const faq = (r, price) => [
  [`Combien coûte un covoiturage ${r.from} – ${r.to} ?`, `Environ ${price} € par place. Le prix est plafonné au coût réel (0,12 €/km divisé par le nombre de places) : le conducteur partage ses frais, il ne fait aucun bénéfice.`],
  [`Combien de temps dure le trajet ${r.from} – ${r.to} en voiture ?`, `Comptez ${DURATION} pour environ ${ROAD_KM} km via l'E19 / A1, hors embouteillages à l'entrée de ${r.to}.`],
  ["Où se font les prises en charge ?", `Au départ de ${r.from} : ${r.fromSpot}. À l'arrivée à ${r.to} : ${r.toSpot}. Le point exact est précisé par le conducteur dans l'annonce.`],
  ["Les conducteurs sont-ils vérifiés ?", "Oui. Permis, carte grise et pièce d'identité sont contrôlés manuellement par l'équipe Nold Go. Le badge « Vérifié » et les avis sont visibles avant de réserver."],
  ["Puis-je annuler ma réservation ?", "Oui, jusqu'à 24 h avant le départ avec remboursement intégral. Si le conducteur annule, vous êtes toujours remboursé."],
];

const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }); } catch { return d; } };

function TripCard({ t }) {
  return (
    <Link to={`/covoiturage/trajet/${t.id}`} data-testid={`route-trip-${t.id}`}
      className="group flex items-center justify-between gap-4 bg-white rounded-2xl p-4 border border-[#E2DFD8] hover:border-[#059669] transition-colors">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-bold text-[#1A1A1A]">
          <CalendarDays className="w-4 h-4 text-[#F26419]" /> {fmtDate(t.date)} · {t.time}
        </div>
        <div className="mt-2"><DriverSnapshot trip={t} compact /></div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-head text-2xl font-black text-[#059669]">{Number(t.price_per_seat_eur || 0).toFixed(0)} €</div>
        <div className="text-xs text-[#75807A]">{t.seats_left} place{t.seats_left > 1 ? "s" : ""}</div>
      </div>
    </Link>
  );
}

function LiveTrips({ r, slug }) {
  const [trips, setTrips] = useState(null);
  useEffect(() => {
    setTrips(null);
    api.get("/carpool/search", { params: { from_city: r.from, to_city: r.to } })
      .then((res) => { const today = new Date().toISOString().slice(0, 10); setTrips((res.data.trips || []).filter((t) => t.date >= today)); })
      .catch(() => setTrips([]));
  }, [r.from, r.to]);

  if (trips === null) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#059669]" /></div>;
  if (!trips.length) return (
    <div data-testid="route-no-trips" className="bg-white rounded-2xl p-6 border border-dashed border-[#E2DFD8]">
      <p className="font-bold text-[#1A1A1A]">Aucun trajet publié pour le moment.</p>
      <p className="text-sm text-[#3B423E] mt-1">Vous partez en voiture ? Soyez le premier à proposer {r.from} → {r.to} et partagez vos frais.</p>
      <Link to="/covoiturage/mes-alertes" data-testid="route-alert-link" className="inline-flex mt-4 text-sm font-bold text-[#059669] hover:underline">Créer une alerte trajet →</Link>
    </div>
  );
  return (
    <div className="grid gap-3" data-testid="route-trips-list">
      {trips.slice(0, 6).map((t) => <TripCard key={t.id} t={t} />)}
      {trips.length > 6 && <Link to="/covoiturage" data-testid="route-see-all" className="text-sm font-bold text-[#059669] hover:underline">Voir les {trips.length} trajets →</Link>}
    </div>
  );
}

export default function CarpoolRoute({ slug }) {
  const r = ROUTES[slug];
  const price = Math.round((ROAD_KM * 0.12) / SEATS_REF);
  const items = faq(r, price);
  const title = `Covoiturage ${r.from} – ${r.to} dès ${price} € | Nold Go`;
  const description = `Covoiturage ${r.from} → ${r.to} : environ ${price} € la place, ${DURATION} de route, conducteurs vérifiés et paiement sécurisé. Réservez ou publiez votre trajet sur Nold Go.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "FAQPage", mainEntity: items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Covoiturage", item: "https://www.noldgo.com/covoiturage/decouvrir" },
        { "@type": "ListItem", position: 2, name: `${r.from} – ${r.to}`, item: `https://www.noldgo.com/covoiturage/${slug}` },
      ] },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid={`carpool-route-${slug}`}>
      <SEOHead title={title} description={description} canonical={`/covoiturage/${slug}`} jsonLd={jsonLd} />

      <section className="max-w-6xl mx-auto px-4 pt-14 pb-10 sm:pt-20">
        <nav className="text-xs text-[#75807A] mb-6" aria-label="Fil d'Ariane">
          <Link to="/covoiturage/decouvrir" className="hover:text-[#059669]">Covoiturage</Link> / <span className="text-[#1A1A1A] font-semibold">{r.from} – {r.to}</span>
        </nav>
        <p className="text-xs uppercase tracking-widest text-[#F26419] font-bold mb-3">Covoiturage longue distance</p>
        <h1 data-testid="route-title" className="font-head text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A1A1A] leading-tight">
          {r.from} <ArrowRight className="inline w-8 h-8 sm:w-12 sm:h-12 text-[#059669] -mt-1" /> {r.to}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#3B423E] max-w-2xl">
          Partagez la route entre {r.from} et {r.to} avec des conducteurs vérifiés. Prix au coût réel, paiement sécurisé par carte.
        </p>

        <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl" data-testid="route-facts">
          {[[Euro, `~${price} €`, "par place"], [Clock, DURATION, "de route"], [MapPin, `${ROAD_KM} km`, "via E19 / A1"]].map(([I, v, l]) => (
            <div key={l} className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
              <I className="w-5 h-5 text-[#F26419] mb-2" />
              <div className="font-head text-xl sm:text-2xl font-black text-[#1A1A1A]">{v}</div>
              <div className="text-xs text-[#75807A]">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/covoiturage" data-testid="route-cta-search" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm transition-colors">
            <Car className="w-4 h-4" /> Rechercher une date
          </Link>
          <Link to="/covoiturage/publier" data-testid="route-cta-publish" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] font-bold text-sm hover:bg-[#F26419]/5 transition-colors">
            <Euro className="w-4 h-4" /> Publier un trajet
          </Link>
          <Link to={`/covoiturage/${r.reverse}`} data-testid="route-reverse-link" className="inline-flex items-center gap-2 px-4 py-3 rounded-full text-sm font-bold text-[#3B423E] hover:text-[#059669] transition-colors">
            <ArrowLeftRight className="w-4 h-4" /> Trajet retour {r.to} → {r.from}
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="font-head text-2xl sm:text-3xl font-black text-[#1A1A1A] mb-5">Prochains départs {r.from} → {r.to}</h2>
        <LiveTrips r={r} slug={slug} />
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          [Euro, "Moins cher que le train", `Un aller ${r.from} – ${r.to} en covoiturage revient à environ ${price} €, souvent bien moins qu'un billet de dernière minute.`],
          [ShieldCheck, "Conducteurs vérifiés", "Permis, carte grise et identité contrôlés. Avis visibles avant chaque réservation."],
          [Users, "Points de rendez-vous pratiques", `${r.fromSpot} — choisis par le conducteur dans l'annonce.`],
        ].map(([I, t, d]) => (
          <div key={t} className="bg-white rounded-2xl p-6 border border-[#E2DFD8]">
            <div className="w-11 h-11 rounded-2xl bg-[#059669]/10 flex items-center justify-center mb-3"><I className="w-5 h-5 text-[#059669]" /></div>
            <h3 className="font-head font-black text-lg text-[#1A1A1A] mb-1">{t}</h3>
            <p className="text-sm text-[#3B423E]">{d}</p>
          </div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-4 py-10">
        <h2 className="font-head text-2xl sm:text-3xl font-black text-[#1A1A1A] mb-5">Questions fréquentes</h2>
        <div className="space-y-3" data-testid="route-faq">
          {items.map(([q, a], i) => (
            <details key={q} data-testid={`route-faq-${i}`} className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
              <summary className="font-bold cursor-pointer text-[#1A1A1A]">{q}</summary>
              <p className="mt-2 text-sm text-[#3B423E]">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-14 text-center">
        <h2 className="font-head text-3xl font-black text-[#1A1A1A] mb-3">Vous faites {r.from} → {r.to} en voiture ?</h2>
        <p className="text-[#3B423E] mb-6">Publiez votre trajet en 2 minutes et partagez vos frais d'essence et de péage.</p>
        <Link to="/covoiturage/publier" data-testid="route-cta-publish-bottom" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#F26419] hover:bg-[#D85312] text-white font-bold transition-colors">
          Publier un trajet <ArrowRight className="w-5 h-5" />
        </Link>
      </section>
    </div>
  );
}
